import axios from 'axios';
import { Capacitor } from '@capacitor/core';

// In a bundled native app the web origin is capacitor://localhost (iOS) or
// https://localhost (Android), so a relative "/api" would resolve to the app
// bundle, not the backend. Force the absolute backend URL when running natively.
const NATIVE_API_URL = 'https://dreamybedtime.com/api';
const resolveBaseURL = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  try { if (Capacitor.isNativePlatform()) return NATIVE_API_URL; } catch {}
  return '/api';
};

const API = axios.create({ baseURL: resolveBaseURL() });

// ── In-memory token store ─────────────────────────────────────────────────────
// The JWT never touches localStorage — it lives only in this module-level
// variable. This eliminates the XSS token-theft vector while remaining fully
// compatible with cross-origin backends and the TWA Android app.
// The trade-off: the token is lost on page refresh, so AuthContext always
// re-validates with GET /auth/me on mount (token returned alongside the user).
let _token = null;

export const setAuthToken = (token) => { _token = token || null; };
export const clearAuthToken = () => { _token = null; };

// Safe localStorage wrapper — used only for the non-sensitive user object cache.
const storage = {
  remove: (key) => { try { localStorage.removeItem(key); } catch {} },
};

// Guards to ensure only one redirect fires even when multiple requests fail simultaneously
let _redirecting = false;
const redirectOnce = (url) => {
  if (_redirecting) return;
  _redirecting = true;
  clearAuthToken();
  storage.remove('dreamybedtime_user');
  window.location.href = url;
};

// Where the user was, so signing back in returns them there. PrivateRoute
// already passes ?redirect= when it bounces someone to the login page, but a
// session expiring mid-visit came through here instead and dropped it — so a
// parent whose token lapsed while reading a story was returned to the dashboard
// afterwards, not to the story. LoginPage reads ?redirect= and only follows
// same-origin paths.
const loginUrlPreservingPath = (extra = '') => {
  const path = `${window.location.pathname}${window.location.search}`;
  const base = `/login${extra}`;
  // Nothing to preserve for the auth pages themselves, or for the root.
  if (/^\/(login|register)\b/.test(path) || path === '/') return base;
  const sep = extra ? '&' : '?';
  return `${base}${sep}redirect=${encodeURIComponent(path)}`;
};

API.interceptors.request.use((config) => {
  if (_token) config.headers.Authorization = `Bearer ${_token}`;
  if (process.env.NODE_ENV !== 'production') {
    config.headers['ngrok-skip-browser-warning'] = '1';
  }
  config.headers['Cache-Control'] = 'no-cache';
  config.headers['Pragma'] = 'no-cache';
  return config;
});

API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (!err.response) {
      const msg = 'Unable to reach the server. Please check your connection.';
      err.userMessage = msg;
      window.dispatchEvent(new CustomEvent('api:error', { detail: { message: msg } }));
      return Promise.reject(err);
    }

    const status = err.response.status;

    if (status === 401) {
      if (err.response.data?.kicked) {
        redirectOnce(loginUrlPreservingPath('?kicked=1'));
      } else {
        redirectOnce(loginUrlPreservingPath());
      }
      return Promise.reject(err);
    }

    if (status === 403 && err.response.data?.suspended) {
      const reason = encodeURIComponent(err.response.data?.message || '');
      redirectOnce(`/suspended?reason=${reason}`);
      return Promise.reject(err);
    }

    if (status === 503 && err.response.data?.maintenance) {
      redirectOnce('/maintenance');
      return Promise.reject(err);
    }

    if (status === 429) {
      const msg = err.response.data?.message || 'Too many requests. Please wait a moment and try again.';
      err.userMessage = msg;
      window.dispatchEvent(new CustomEvent('api:ratelimit', { detail: { message: msg } }));
      return Promise.reject(err);
    }

    if (status >= 500) {
      const msg = err.response.data?.message || 'A server error occurred. Please try again shortly.';
      err.userMessage = msg;
      window.dispatchEvent(new CustomEvent('api:error', { detail: { message: msg } }));
    }

    return Promise.reject(err);
  }
);

export default API;
